import { useTheme } from 'next-themes';
import {
  forwardRef,
  type HTMLAttributes,
  useEffect,
  useMemo,
  useState,
} from 'react';

export interface ThemedImgProps extends Omit<
  HTMLAttributes<HTMLImageElement>,
  'src'
> {
  lightSrc: string;
  darkSrc?: string;
  fallbackSrc?: string;
}

export const ThemedImg = forwardRef<HTMLImageElement, ThemedImgProps>(
  function ThemedImg(
    { lightSrc, darkSrc, fallbackSrc, onError, ...attrs },
    ref
  ) {
    const { resolvedTheme, theme } = useTheme();
    const domTheme =
      typeof document === 'undefined'
        ? undefined
        : document.documentElement.getAttribute('data-theme');
    const isDark =
      resolvedTheme === 'dark' ||
      resolvedTheme === 'blue-white' ||
      theme === 'blue-white' ||
      domTheme === 'blue-white';
    const orderedSources = useMemo(() => {
      const preferredSrc = isDark && darkSrc ? darkSrc : lightSrc;
      const alternateSrc = preferredSrc === lightSrc ? darkSrc : lightSrc;
      return [preferredSrc, alternateSrc, fallbackSrc].filter(
        (src, index, sources): src is string =>
          Boolean(src) && sources.indexOf(src) === index
      );
    }, [darkSrc, fallbackSrc, isDark, lightSrc]);
    const [failedSources, setFailedSources] = useState<ReadonlySet<string>>(
      () => new Set()
    );

    useEffect(() => {
      setFailedSources(new Set());
    }, [darkSrc, fallbackSrc, lightSrc]);

    const src =
      orderedSources.find(src => !failedSources.has(src)) ??
      orderedSources[orderedSources.length - 1] ??
      lightSrc;

    return (
      <img
        ref={ref}
        src={src}
        {...attrs}
        onError={event => {
          setFailedSources(previous => {
            if (previous.has(src)) {
              return previous;
            }
            return new Set(previous).add(src);
          });
          onError?.(event);
        }}
      />
    );
  }
);
